import React, { useState, useEffect, useRef, useMemo, memo, useCallback } from 'react';
import { ConnectionPageShared } from './ConnectionPageShared';
import PendingProfileOverlay from '../Overlay/PendingProfileOverlay';
import LaunchSignupOverlay from '../Overlay/LaunchSignupOverlay';
import AnonShareOverlay from '../Overlay/AnonShareOverlay';
import ChatInputMorphing from '../UISystem/Chat/ChatInputMorphing';
import AnonThreadGate from './variants/thread/AnonThreadGate';
import AnonThreadShareSheet from './variants/thread/AnonThreadShareSheet';
import IosHandoffSheet from '../Overlay/IosHandoffSheet';
import ThreadPeopleBadge from './variants/thread/ThreadPeopleBadge';
import { isThreadVariant } from '../../utils/exchangeVariant';
import { buildPermanentUrl, getHostProfileUserId } from '../../utils/permanentUrl';
import { openReminderIcs } from '../../services/sharing/reminderIcs';
import { WelcomeInviteBanner } from '../UISystem/Chat/WelcomeInviteBanner';
import { Broom } from '@phosphor-icons/react/dist/ssr/Broom';
import { Check } from '@phosphor-icons/react/dist/ssr/Check';
import { useNavigation } from '../../contexts/NavigationContext';
import { useSession } from '../../contexts/SessionContext';
import { useConnectionData, useConnectionContext } from '../../contexts/ConnectionStateContext';
import { joinChatRoom, sendMessage } from '../../services/chat/chatService';
import {
  getConnectionMetadata,
  getAnonData,
  setLastReadTimestamp,
} from '../../services/storage/userDataStorage';
import {
  logFunnelSignupWaitlist,
  logFunnelInviteClicked,
  logFunnelExchangeCompleted,
  logEvent,
} from '../../services/analytics/eventLogger';
import { firstNameOf } from '../../utils/contactUtils';
import { isIOS } from '../../utils/platformUtils';
import { formatLocationShort, formatSystemMessageLocation } from '../../utils/locationFormatters';
import { getOrCreateAnonUserId, getAnonUserId } from '../../utils/identityUtils';
import { persistAnonName, commitAnonContactRecord } from '../../services/sharing/anonShareBack';
import useHeartbeat from '../../hooks/useHeartbeat';
import usePeerStatus from '../../hooks/usePeerStatus';
import {
  buildAnonContactDataMessage,
  buildImageChatMessage,
  buildSentChatMessage,
  buildSystemMeetingMessage,
} from '../../services/chat/messageBuilders';
import {
  classifyContactInput,
  extractNameFromEntry,
  looksLikePlainName,
  reciprocalContactTypes,
} from '../../utils/contactClassify';
import { deriveBannerVariant, shouldDockBanner } from '../../utils/bannerVariant';
import { getTime } from '../../utils/chatHelpers';
import { ANON_CHAT_EXPIRY_MS, anonChatExpiryPhrase } from '../../utils/lifecycleWindows';
import ExpiredChatScreen from './ExpiredChatScreen';
import { formatPhoneNumber } from '../../utils/formatters';
import { uploadChatImage } from '../../services/storage/chatImageUpload';
import { persistMessage } from '../../services/chat/messageStore';
import { getConnectionStatus } from '../../services/activeSession';
import { getConnectionCount } from '../../services/storage/connectionStorage';
import { hasData } from '../../utils/objectHelpers';
import { COLORS, BORDER_RADIUS } from '../../styles/UISystem';
import { CROWN_BLACK } from '../../styles/brandAssets';
import { motion } from 'framer-motion';
import NoticeCard from '../UISystem/NoticeCard/NoticeCard';
import useGuestSaveRetry from '../../hooks/useGuestSaveRetry';

// D7 device toggle: the unlock pill is NOT black (user call) — grey vs
// outlined decided on device. Flip to 'outlined' for the comparison pass.
const UNLOCK_PILL_VARIANT = 'grey';

// persistAnonName / commitAnonContactRecord / dispatchAnonDataUpdated moved to
// services/sharing/anonShareBack.js (tiered-exchange Step 3 lift) — this page
// delegates so the sans-chat flow and the chat flow share ONE copy.

/**
 * Connection page specifically for anonymous users
 * Displays profile data that has been shared with the anonymous user
 *
 * @param {Object} props - Component props
 * @param {string} props.source - 'qr-scan' when rendered from AnonExchangeFlow (parent manages WebSocket)
 */
const AnonConnectionPage = ({
  source = null, // 'qr-scan' when rendered from AnonExchangeFlow (parent manages WebSocket)
  // Fix: chatRoomId passed as prop from AnonExchangeFlow to avoid race condition
  chatRoomId: propChatRoomId = null,
  // Fix 5: Props passed from AnonExchangeFlow when source='qr-scan'
  // This prevents context subscription re-renders - memo() will actually work
  profileData: propProfileData = null,
  connectionMetadata: propConnectionMetadata = null,
  chatMessages: propChatMessages = null,
  updateProfileData: propUpdateProfileData = null,
  updateConnectionMetadata: propUpdateConnectionMetadata = null,
  updateChatMessages: propUpdateChatMessages = null,
  // Connection error state for error UX (timeout, websocket failures)
  connectionError: propConnectionError = null,
  // Landing → chat reveal (2026-07-27): stagger the first N waiting messages
  // in one after another; forwarded to ConnectionPageShared
  entranceStaggerCount = 0,
}) => {
  // Use navigation context for navigation functions
  const { navigateTo, goBack } = useNavigation();

  // Use session context as the single source of truth for chatRoomId
  const { sessionChatRoomId, updateSessionChatRoomId } = useSession();

  // Fix: When source='qr-scan', use prop chatRoomId to avoid race condition
  // Props are available immediately, sessionChatRoomId may not be set on first render
  // Also used for Fix 5: props instead of context to prevent re-renders
  const isFromQRScan = source === 'qr-scan';
  const chatRoomId = isFromQRScan ? propChatRoomId || sessionChatRoomId : sessionChatRoomId;

  // Connection confirmation status — polls every 3s, max 4 attempts.
  const [connectionStatus, setConnectionStatus] = useState(null);
  // The guest's own writes that didn't land (guest-session-loss): said on
  // screen with Try again, never dropped silently (shared hook, E3a)
  const { unsaved, retrying, retryUnsaved } = useGuestSaveRetry(chatRoomId);

  // DB heartbeat presence — true when peer's heartbeat is fresh. Reads the id
  // without minting: it was minted + claimed at the scan (ensureAnonIdentity),
  // and a mount is never a commit action.
  const peerOnline = useHeartbeat(chatRoomId, getAnonUserId());

  // Derive connection status for deleted banner logic
  const dotStatus = useMemo(() => {
    if (!connectionStatus) return null;
    if (connectionStatus === 'deleted') return 'deleted';
    return 'offline';
  }, [connectionStatus]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!chatRoomId) return;
    let stopped = false;
    let failures = 0;

    const poll = async () => {
      const status = await getConnectionStatus(chatRoomId);
      if (stopped) return;
      setConnectionStatus(status);
      if (status === 'confirmed' || status === 'deleted') {
        stopped = true;
        return;
      }
      failures++;
      if (failures >= 4) stopped = true;
    };

    poll(); // immediate first check
    const interval = setInterval(() => {
      if (stopped) return clearInterval(interval);
      poll();
    }, 3000);

    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [chatRoomId]);

  // Re-check status when peer comes online — catches confirmations that happened
  // after the initial poll window (e.g., missed connection recovery)
  useEffect(() => {
    if (
      peerOnline &&
      connectionStatus &&
      connectionStatus !== 'confirmed' &&
      connectionStatus !== 'deleted'
    ) {
      getConnectionStatus(chatRoomId).then(setConnectionStatus);
    }
  }, [peerOnline, connectionStatus, chatRoomId]);

  // Always subscribe to context — provides data on reload and DB background sync.
  // Props from AnonExchangeFlow provide immediate display on fresh scans.
  const contextData = useConnectionData(chatRoomId);

  // Get context update methods — always available (props override when present)
  const contextMethods = useConnectionContext();

  // User preferences and setters - always available (not dependent on isFromQRScan)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const {
    updateContactSharingStatus,
    userPreferences,
    setHasSignedUpForQR,
    setWaitlistHeld,
    setCurrentChatRoomId,
    dismissBanner,
  } = useConnectionContext();

  // NOTE: useConnectionData hook handles loadConnectionData automatically for all modes.
  // No explicit load effect needed — context loads from localStorage on mount and syncs from DB in background.

  // Resolve update methods: props take priority (from QR scan), fallback to context
  const updateProfileData = propUpdateProfileData || contextMethods?.updateProfileData;
  const updateConnectionMetadata =
    propUpdateConnectionMetadata || contextMethods?.updateConnectionMetadata;
  const updateChatMessages = propUpdateChatMessages || contextMethods?.updateChatMessages;

  // Resolve data: props provide immediate display (QR scan), context provides persistence (reload/DB sync)
  const profileData = propProfileData || contextData?.profileData || null;
  const connectionMetadata = propConnectionMetadata || contextData?.connectionMetadata || null;

  // Peer activity status for the chat header — heartbeat + last_active_at.
  // Anon users don't see peer location (fetchLocation: false).
  const { activityStatus } = usePeerStatus(profileData?.profileUserId, chatRoomId, {
    peerOnline,
    fetchLocation: false,
  });

  // Local state to track messages received via WebSocket after initial load
  // This is needed because when isFromQRScan is true, we read from props which don't update
  const [localWebSocketMessages, setLocalWebSocketMessages] = useState([]);

  // Memoize to prevent useEffect dependency warning
  // Merge prop messages with locally received WebSocket messages
  const chatMessagesFromSource = useMemo(() => {
    // Context is the source of truth (backed by DB). Props are only for the first render before context loads.
    const propMsgs = propChatMessages || [];
    const contextMsgs = contextData?.chatMessages || [];
    const baseMessages = contextMsgs.length > 0 ? contextMsgs : propMsgs;
    // Merge with local WebSocket messages, avoiding duplicates by id or timestamp
    const mergedMessages = [...baseMessages];
    localWebSocketMessages.forEach((wsMsg) => {
      const isDuplicate = mergedMessages.some(
        (msg) =>
          (msg.id && msg.id === wsMsg.id) ||
          (msg.timestamp === wsMsg.timestamp &&
            msg.type === wsMsg.type &&
            msg.sender === wsMsg.sender)
      );
      if (!isDuplicate) {
        mergedMessages.push(wsMsg);
      }
    });

    return mergedMessages;
  }, [propChatMessages, contextData?.chatMessages, localWebSocketMessages]);

  // Use ref to track latest messages without causing callback to recreate
  const messagesRef = React.useRef([]);
  React.useEffect(() => {
    messagesRef.current = chatMessagesFromSource || [];
  }, [chatMessagesFromSource]);

  // NOTE: DB sync is now handled by ConnectionStateContext.loadConnectionData()
  // which reads from localStorage cache first (instant render), then syncs from DB in background.
  // This eliminates the flicker that occurred when this component did its own async DB fetch.

  // Mark chat as read when entering this page AND when new messages arrive
  // This ensures messages seen in real-time don't show as unread on the list
  useEffect(() => {
    if (chatRoomId) {
      setLastReadTimestamp(chatRoomId);
      // Sync currentChatRoomId so userPreferences.hasSignedUpForQR loads for this session
      setCurrentChatRoomId(chatRoomId);
    }
  }, [chatRoomId, chatMessagesFromSource, setCurrentChatRoomId]);

  // Wrapper setter that persists messages to context AND updates local state for immediate display.
  // Persistence uses updater pattern (always reads latest state inside setConnections).
  // Local diff uses ref — less critical, worst case is a duplicate that gets deduplicated.
  const handleSetChatMessages = React.useCallback(
    (updater) => {
      // Persist to context (updater pattern — always reads latest state)
      if (updateChatMessages) {
        updateChatMessages(chatRoomId, updater);
      }

      // Track added messages locally for QR scan immediate display
      const currentMessages = messagesRef.current;
      const newMessages = typeof updater === 'function' ? updater(currentMessages) : updater;
      const addedMessages = newMessages.filter(
        (newMsg) =>
          !currentMessages.some(
            (oldMsg) =>
              (oldMsg.id && oldMsg.id === newMsg.id) ||
              (oldMsg.timestamp === newMsg.timestamp &&
                oldMsg.type === newMsg.type &&
                oldMsg.sender === newMsg.sender)
          )
      );
      if (addedMessages.length > 0) {
        setLocalWebSocketMessages((prev) => [...prev, ...addedMessages]);
      }
    },
    [chatRoomId, updateChatMessages]
  );

  // Always set isLoading to false - we never want loading indicators
  const [isLoading] = useState(false);

  // The share/chat toggle (anonInputMode) retired with the three-state field
  // (social-track refine Q6): unclassified input IS chat, so there are no modes.

  // Share overlay — now the card-tap EDIT surface only (pre-filled name +
  // contact). Retired from the share flow: the landing decision sheet is gone
  // (land directly in the room) and first-share runs through the morphing input.
  const [showShareOverlay, setShowShareOverlay] = useState(false);

  // Overlay states — waitlist (LaunchSignupOverlay) and PWA conversion (PendingProfileOverlay)
  const [showSignupOverlay, setShowSignupOverlay] = useState(false);
  const [showPendingOverlay, setShowPendingOverlay] = useState(false);

  // Track name and contact if already shared.
  // Synchronous localStorage init prevents false→true flicker that replays card animation.
  const [alreadySharedName, setAlreadySharedName] = useState(() => {
    const rid = propChatRoomId || sessionChatRoomId;
    if (!rid) return '';
    try {
      const data = JSON.parse(localStorage.getItem(`anon_user_data_${rid}`) || '{}');
      return data?.name || '';
    } catch {
      return '';
    }
  });
  const [alreadySharedTypes, setAlreadySharedTypes] = useState(() => {
    const rid = propChatRoomId || sessionChatRoomId;
    if (!rid) return [];
    try {
      const data = JSON.parse(localStorage.getItem(`anon_user_data_${rid}`) || '{}');
      return data?.sharedFields || [];
    } catch {
      return [];
    }
  });
  const [anonPhone, setAnonPhone] = useState('');
  // Signup opened from the expired screen → thanks renders full-page (decision #28)
  const [signupFromExpired, setSignupFromExpired] = useState(false);
  const [sharedContactValues, setSharedContactValues] = useState(() => {
    const rid = propChatRoomId || sessionChatRoomId;
    if (!rid) return {};
    try {
      const data = JSON.parse(localStorage.getItem(`anon_user_data_${rid}`) || '{}');
      const values = {};
      (data?.sharedFields || []).forEach((type) => {
        if (data[type]) values[type] = data[type];
      });
      return values;
    } catch {
      return {};
    }
  });

  // Fix 5: Use resolved data from props (QR scan) or context (standalone)
  const chatMessages = useMemo(() => chatMessagesFromSource || [], [chatMessagesFromSource]);
  // The real-access invite (if any) — single source of truth for the banner + Join handler.
  const inviteMsg = useMemo(() => chatMessages.find((m) => m.isInviteCta) || null, [chatMessages]);

  // MIGRATION STEP 5: Removed redundant localStorage loading useEffect
  // All data loading is now handled by useConnectionData hook via ConnectionStateContext.loadConnectionData
  // The context automatically loads profileData, connectionMetadata, notes, and hasSharedContactInfo from localStorage

  // Load anon's own data on mount — name, phone, sharedFields.
  // Uses getAnonData (anon's own store), NOT contextData.anonData (which loads from
  // getReceivedAnonData — the Profile-side store, empty on the Anon side).
  useEffect(() => {
    const loadSharedName = async () => {
      if (!chatRoomId) return;
      const anonData = await getAnonData(chatRoomId);
      if (anonData?.name) {
        setAlreadySharedName(anonData.name);
      }
      if (anonData?.phone) {
        setAnonPhone(anonData.phone);
      }
      if (anonData?.sharedFields) {
        setAlreadySharedTypes(anonData.sharedFields);
        // Track all contact values for edit overlay prefill
        const values = {};
        anonData.sharedFields.forEach((type) => {
          if (anonData[type]) values[type] = anonData[type];
        });
        setSharedContactValues(values);
      }
    };
    loadSharedName();
  }, [chatRoomId]);

  // Note: Signup overlay is on AnonConnectionsListPage, triggered by "Get my QR" button
  // User navigates there after completing exchange flow

  // Local system message creation — provides immediate display while DB sync brings
  // the authoritative version. ProfileConnectionPage is the sole DB persister;
  // this only calls updateChatMessages (no persistMessage).
  const lastSystemContentRef = useRef(null);

  useEffect(() => {
    if (!connectionMetadata) return;
    // Time-first: the banner needs only a meetingDate. GPS/Radar fail often (indoors,
    // permission, rate limits), so we no longer gate the whole banner on a venue —
    // the time always renders from meetingDate via formatTimePillShort, and the venue
    // is added when/if it resolves. Trades Step 4's no-jump gating for a banner that
    // never fully disappears. (ChatMessage shows the pin only when a venue line exists.)
    if (!connectionMetadata.meetingDate) return;
    if (!updateChatMessages) return;

    // Venue when available, else '' — render falls back to the time-only line.
    const systemMessageContent = formatSystemMessageLocation(connectionMetadata);

    // Prevent re-write loops
    if (systemMessageContent === lastSystemContentRef.current) return;

    const existingSystemMsg = chatMessages.find((msg) => msg.type === 'system');

    if (existingSystemMsg) {
      if (existingSystemMsg.content !== systemMessageContent) {
        const updatedSystemMsg = {
          ...existingSystemMsg,
          content: systemMessageContent,
          meetingDate: connectionMetadata.meetingDate,
          meetingUtcOffset: connectionMetadata.meetingUtcOffset,
        };
        const updatedMessages = chatMessages.map((msg) =>
          msg.type === 'system' ? updatedSystemMsg : msg
        );
        updateChatMessages(chatRoomId, updatedMessages);
      }
      lastSystemContentRef.current = systemMessageContent;
      return;
    }

    // Create new system message for local display only (no DB persist)
    const systemMessage = buildSystemMeetingMessage({
      id: `system_${chatRoomId}`,
      content: systemMessageContent,
      meetingDate: connectionMetadata.meetingDate,
      meetingUtcOffset: connectionMetadata.meetingUtcOffset,
    });

    updateChatMessages(chatRoomId, [systemMessage, ...chatMessages]);
    lastSystemContentRef.current = systemMessageContent;
  }, [connectionMetadata, chatMessages, chatRoomId, updateChatMessages]);

  // Welcome/invite are shown as the docked WelcomeInviteBanner (footer), derived from
  // share / signup / invite / dismiss state (see `bannerVariant` near the render) — no
  // injected chat message, no per-card animation effects.

  // MIGRATION STEP 4: Removed localStorage checking useEffect - context handles this via loadConnectionData
  // The hasSharedContactInfo state is now managed by ConnectionStateContext

  // Note: Name submission is now handled within the ContactShareOverlay component

  // Join chat room and set up message handlers
  // Load initial data and set up chat room when component mounts
  useEffect(() => {
    const loadInitialData = async () => {
      if (!chatRoomId) {
        console.error('CRITICAL: AnonConnectionPage - No chatRoomId provided, cannot load data');
        return;
      }
      try {
        const storedConnectionMetadata = await getConnectionMetadata(chatRoomId);
        // Don't create minimal metadata to avoid incorrect system messages
        if (hasData(storedConnectionMetadata)) {
          console.log('ANON_CONNECTION: Found connection metadata from localStorage');
        } else {
          console.log(
            'ANON_CONNECTION: No connection metadata found, waiting for data from profile user'
          );
        }

        // MIGRATION: No longer setting local state, context will handle this
        // Previously: setConnectionMetadata(storedConnectionMetadata)

        // Don't create system message on initial load - only after contact share
        console.log('ANON_CONNECTION: No system message on load - will appear after contact share');
      } catch (error) {
        console.error('CRITICAL ERROR: AnonConnectionPage - Error loading initial data:', error);
      }
    };

    const joinChatRoomAndSetupHandlers = async () => {
      // When source='qr-scan', AnonExchangeFlow manages messages.
      // Presence is now handled by useHeartbeat — no channel join needed here.
      if (source === 'qr-scan') {
        return;
      }

      if (chatRoomId) {
        // No component-level message handler needed — all persistence handled
        // by global handler in ConnectionStateContext.
        // Channel subscription kept open so global handler receives messages.
        joinChatRoom(chatRoomId, () => {});
      }
    };
    // Call the functions to load initial data and join chat room
    const loadData = async () => {
      await loadInitialData();
      await joinChatRoomAndSetupHandlers();
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // MIGRATION: Removed setProfileData and setConnectionMetadata from dependencies as we're using context now
    // Added updateProfileData, updateConnectionMetadata, and handleSetChatMessages dependencies
  }, [chatRoomId, source, updateProfileData, updateConnectionMetadata, handleSetChatMessages]);

  // anonDataUpdated event — dispatched from contact sharing UI flow (not message handler).
  // Merges anon data into connection metadata for the Anon user's own records.
  useEffect(() => {
    const handleAnonDataUpdate = async (event) => {
      try {
        if (!event.detail || !event.detail.anonData) return;

        const anonData = event.detail.anonData;
        const existingConnectionMetadata = (await getConnectionMetadata(chatRoomId)) || {};

        const updatedConnectionMetadata = {
          ...existingConnectionMetadata,
          anonUserData: anonData,
        };

        await updateConnectionMetadata(chatRoomId, updatedConnectionMetadata);
      } catch (error) {
        console.error('ANON_CONNECTION: Error handling anon data update:', error);
      }
    };

    window.addEventListener('anonDataUpdated', handleAnonDataUpdate);
    return () => window.removeEventListener('anonDataUpdated', handleAnonDataUpdate);
  }, [chatRoomId, updateConnectionMetadata]);

  // No viewport management - let the keyboard naturally overlay on content

  // Handle skip contact share — advance to chat without sharing contact
  // Handle invite CTA click — waitlist (non-whitelisted) or PWA conversion (whitelisted)
  const handleSignupClick = useCallback(() => {
    setSignupFromExpired(false);
    setShowSignupOverlay(true);
  }, []);

  // Expired-screen entry — the success takes the WHOLE page there (decision #28):
  // a success card floating over "Your chat has expired" contradicts itself, and
  // that page is no use once they've signed up. Same content, different container.
  const handleExpiredSignupClick = useCallback(() => {
    setSignupFromExpired(true);
    setShowSignupOverlay(true);
  }, []);

  const handlePendingClick = useCallback(() => {
    setShowPendingOverlay(true);
  }, []);

  // Open the unified share overlay (pill / "+" / card-tap; pre-filled from shared data).
  const openShareOverlay = useCallback(() => {
    setShowShareOverlay(true);
  }, []);

  // Invite banner "Join" → PWA conversion (whitelisted) or waitlist. Routes on the
  // invite message's canCreateProfile; preserves the invite-clicked funnel event.
  const handleBannerJoin = useCallback(() => {
    const canCreate = !!inviteMsg?.canCreateProfile;
    logFunnelInviteClicked(chatRoomId, canCreate);
    if (canCreate) handlePendingClick();
    else handleSignupClick();
  }, [inviteMsg, chatRoomId, handlePendingClick, handleSignupClick]);

  // GetOwnQR handlers removed - now only available on AnonConnectionsListPage

  // Send name data to Profile via WebSocket + save locally
  // Merges with existing data to preserve contacts during name edits.
  // Any explicit name write closes the two-message refinement window.
  const sendNameData = async (name) => {
    const anonData = await persistAnonName(chatRoomId, name);

    // Send data-only message to Profile (skipDisplay = no chat bubble)
    sendMessage(
      chatRoomId,
      buildAnonContactDataMessage({ senderId: getOrCreateAnonUserId(), anonData })
    );

    setAlreadySharedName(name);
    localStorage.removeItem(`name_provisional_${chatRoomId}`);
  };

  // Handle name submission (first share, from the unified overlay).
  // Sends ONLY the name-card chat bubble — no separate name-only `contact` message.
  // That message carried sharedFields:[] and shared the deterministic `contact` row
  // id with the real contact message (now commitAnonContact), so the two
  // raced and the empty one could wipe the Profile's copy of the shared contact
  // (see docs § Bugs Found). The name still reaches the Profile via this message's
  // anonName (global-handler extraction, which merge-preserves) and via the real
  // contact message's anonData.name. The contact submit fires immediately after and
  // is the sole writer of the `contact` row.
  const handleShareBackNameSubmit = async (nameValue, { extractName = false } = {}) => {
    console.log('[ANON_CONNECTION] Share back name submitted:', nameValue);

    const raw = nameValue.trim();
    // Name-greeting extraction (chat name field only — the edit overlay stays
    // verbatim as the correction surface): "Hi it's Franklin" stores Franklin
    // on the record; the bubble below still shows the exact words (#9b).
    const extracted = extractName ? extractNameFromEntry(raw) : null;
    const name = extracted || raw;
    // Provisional name = extraction found nothing AND the raw doesn't read as
    // a plain name ("Hey Mark") — opens the two-message refinement window
    // (the next chat message that extracts replaces it; item 13).
    if (extractName) {
      if (!extracted && !looksLikePlainName(raw)) {
        localStorage.setItem(`name_provisional_${chatRoomId}`, 'true');
      } else {
        localStorage.removeItem(`name_provisional_${chatRoomId}`);
      }
    }

    // Create chat message with name (for display as chat bubble)
    const currentSenderId = getOrCreateAnonUserId();
    const nameDisplayMessage = {
      id: `chat_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      type: 'chat',
      sender: 'anon',
      senderId: currentSenderId,
      content: raw, // verbatim speech — never rewritten by extraction
      anonName: name, // Primary name delivery to Profile (extraction merge-preserves sharedFields)
      timestamp: new Date().toISOString(),
    };

    // Send display message via WebSocket
    sendMessage(chatRoomId, nameDisplayMessage);
    console.log('[ANON_CONNECTION] Name display message sent via WebSocket');

    // Add to local chat state (optimistic update)
    handleSetChatMessages((prevMessages) => {
      console.log('[ANON_CONNECTION] Adding name message to chat state');
      return [...prevMessages, nameDisplayMessage];
    });

    // Persist the anon's own copy (merge-preserving, NO contact data message —
    // the Profile already gets the name via the bubble's anonName extraction).
    // The sequential flow can REST at name-only, and the morph stage re-derives
    // from this store on every mount: without the save, any remount re-asked the
    // name and a re-submit sent a duplicate bubble (found at checkpoint B).
    await persistAnonName(chatRoomId, name);
    setAlreadySharedName(name);
  };

  // Shared record-commit for a classified contact — used by the contact-stage
  // classification AND the edit overlay. ADDITIVE (decision #15, both
  // directions): a new type joins the record, a same-type re-share updates its
  // value, nothing is ever un-shared (supersedes the old single-contact
  // latest-wins clearing). Sends the ONE authoritative `contact` data message
  // per action (deterministic id assigned in chatService) — this message must
  // keep flowing: conversion identity-healing reads self-declared phones from
  // `contact` messages (migration 021).
  const commitAnonContact = async (contactType, contactValue) => {
    const anonData = await commitAnonContactRecord(chatRoomId, contactType, contactValue, {
      fallbackName: alreadySharedName,
    });
    if (!anonData) return; // identical re-share: nothing to change

    // Update local state (additive — merge, don't replace)
    if (contactType === 'phone') {
      setAnonPhone(contactValue);
    }
    setSharedContactValues((prev) => ({ ...prev, [contactType]: contactValue }));
    setAlreadySharedTypes(anonData.sharedFields);

    if (updateContactSharingStatus) {
      await updateContactSharingStatus(chatRoomId, true);
    }
  };

  // Contact-stage submit — everything the Anon types is their SPEECH first
  // (decision #9: the bubble shows it verbatim); classification decides whether
  // a contact record rides along (decision #8). Unclassified input is pure chat
  // and the parked ask stays — the stage ends on the first successful
  // classification (alreadySharedTypes flips morphStage to null).
  const handleContactStageSubmit = async ({ raw, fieldState }) => {
    const text = (raw || '').trim();
    if (!text) return;

    const classified = classifyContactInput(text, {
      fieldState,
      reciprocalTypes: reciprocalContactTypes(profileData),
    });

    // The phone state emits E.164 — the bubble shows it the way the field did
    const bubbleText = fieldState === 'phone' ? formatPhoneNumber(text) : text;

    // Their exact words as the bubble; if classified, the classification rides
    // along (linked render on the receiving side, excluded from freeform-chat
    // analytics) — the shape lives in the builder
    const bubble = buildSentChatMessage({
      senderType: 'anon',
      senderId: getOrCreateAnonUserId(),
      content: bubbleText,
      classified,
    });

    handleSetChatMessages((prev) => [...prev, bubble]);
    sendMessage(chatRoomId, bubble);

    if (classified) {
      await commitAnonContact(classified.type, classified.value);
    } else if (localStorage.getItem(`name_provisional_${chatRoomId}`) === 'true') {
      // Two-message name pattern ("Hey Mark" then "Emily here"): while the
      // stored name is provisional, a chat message that extracts refines the
      // record through the existing name-edit path (bubble stays verbatim).
      // Chat-tightened triggers — "this is fun" must never become a name.
      const refined = extractNameFromEntry(text, { fromChat: true });
      if (refined) {
        await sendNameData(refined);
      }
    }
  };

  // Unified submit from AnonShareOverlay — the card-tap EDIT surface. First
  // share still sends the name chat bubble; later name edits go data-only.
  // Contact edits commit SILENTLY (decision #13: chat shares speak, record
  // edits don't — the overlay is an edit surface, not speech).
  const handleUnifiedShareSubmit = async ({ name, contactType, contactValue }) => {
    const isFirstShare = !alreadySharedName && alreadySharedTypes.length === 0;
    if (isFirstShare) {
      await handleShareBackNameSubmit(name);
    } else if (name && name !== alreadySharedName) {
      await sendNameData(name);
      setAlreadySharedName(name);
    }
    if (contactValue) {
      await commitAnonContact(contactType, contactValue);
    }
    setShowShareOverlay(false);
  };

  // Handle photo capture — optimistic display, background upload, in-place update
  // Uses handleSetChatMessages (always available) instead of propUpdateLocalMessage
  // (only available from QR scan flow) so photos work from connections list too.
  const handleTakePhoto = useCallback(
    async (file) => {
      const senderId = getOrCreateAnonUserId();
      const blobUrl = URL.createObjectURL(file);

      // Optimistic message with blob URL
      const message = buildImageChatMessage({
        senderId,
        senderType: 'anon',
        imageUrl: blobUrl,
        uploading: true,
      });

      // Display immediately in chat
      handleSetChatMessages((prev) => [...prev, message]);

      try {
        // Compress and upload
        const publicUrl = await uploadChatImage(file, chatRoomId);

        // Update message in-place — swap blob URL for real URL
        handleSetChatMessages((prev) =>
          prev.map((msg) =>
            msg.id === message.id ? { ...msg, content: publicUrl, uploading: undefined } : msg
          )
        );

        // Build the final message for sending
        const finalMessage = { ...message, content: publicUrl, uploading: undefined };

        // Send to other user via WebSocket
        await sendMessage(chatRoomId, finalMessage);

        // Persist to DB
        await persistMessage(chatRoomId, finalMessage);

        // Revoke blob URL
        URL.revokeObjectURL(blobUrl);
      } catch (err) {
        console.error('[AnonConnectionPage] Photo upload failed:', err);
        handleSetChatMessages((prev) =>
          prev.map((msg) =>
            msg.id === message.id ? { ...msg, uploadFailed: true, uploading: undefined } : msg
          )
        );
      }
    },
    [chatRoomId, handleSetChatMessages]
  );

  // Always show standard ChatInputNormal — overlays float over it (z-index 1000).

  // Handle profile image click to navigate to detail page
  const handleImageClick = () => {
    console.log('[ANON_CONNECTION] Profile image clicked, navigating to detail page');
    updateSessionChatRoomId(chatRoomId);
    navigateTo('anon-connection-detail', { chatRoomId, source: 'connection-page' });
  };

  // Always show full profileData regardless of sharing status
  // Contact icons and metadata should always be visible
  const displayProfileData = profileData;

  // Morphing input stage — derived synchronously from prior-share state, so a
  // returning Anon lands on the right stage without re-gating (refine pass 1).
  // null = a contact classified → standard composer (the morph hands back).
  // The morph owns the composer slot for the whole pre-contact window now —
  // the share/chat toggle retired with the three-state field (unclassified
  // input IS chat, so there is nothing to toggle).
  const hasSharedContact = alreadySharedTypes.length > 0;
  const morphStage = !alreadySharedName ? 'name' : !hasSharedContact ? 'contact' : null;

  // THREAD's HARD lock (plan 3 Step 5). Same unlock signal as the morph's
  // final stage — a committed contact record — so D5 holds by construction:
  // the derivation is identity-based room state, never a visit flag, and a
  // returner who already shared (including one arriving via the ICS link days
  // later) lands on an unlocked composer with no gate.
  //
  // Difference from CHAT: the morph keeps a typeable field throughout and
  // hands back post-classification. THREAD REMOVES the composer and puts the
  // gate CTA in the slot, so there is exactly one thing to do.
  const threadLocked = isThreadVariant() && !hasSharedContact;

  // THREAD: hold the moment banner until the host has actually answered "where
  // you met". At formation the venue is only a GPS guess, so the Anon was
  // seeing "Met in Greenwich Village" and then watching it become "Met at
  // Carmela Coffee" — a wrong fact, corrected in front of them. Waiting makes
  // the host's confirmation the thing that puts the moment in the thread.
  //
  // The trigger is the VENUE ANSWER alone (user ruling 2026-08-15) — the
  // banner lands when the host picks where they met and sends, not alongside
  // the contact card. All three rows of the host's venue sheet count as an
  // answer, the neighborhood row included; it clears the venue and confirms,
  // so the sentence falls to "Met in {area}".
  const hideMeetingBanner = isThreadVariant() && connectionMetadata?.venueConfirmed !== true;

  // ===== THREAD's structured share (plan 3 Step 6) =====
  // Two submits, two DIFFERENT message types — the contract refine's Gaps pass
  // corrected. Both rails already exist, so this composes them rather than
  // adding writers: the NAME goes through handleShareBackNameSubmit, which
  // already sends a `chat` message carrying anonName (the global handler
  // read-modify-writes it, so sharedFields is merge-preserved), and the VALUE
  // is the single authoritative `contact` write via commitAnonContact.
  //
  // Why not announceAnonName + commitAnonContactRecord: both emit a `contact`
  // message under the same deterministic id, so they upsert ONE row and the
  // second silently replaces the first — the mocks' two persistent bubbles
  // would have been unbuildable, and the same-id race is a logged gotcha.
  const [showThreadSheet, setShowThreadSheet] = useState(false);

  // ===== Remind me later (Step 8, D10) + the People pill (D11) =====
  // ONE per-room localStorage sentinel serves both: tapping "Remind me later"
  // retires the link AND is the declaration of "not now, let me look around"
  // that reveals the People pill. localStorage, never session state — a return
  // through the calendar link days later means the reminder did its job and
  // must not be re-offered, and the pill must still be there.
  const reminderKey = `thread_reminder_set_${chatRoomId}`;
  const [threadDeferred, setThreadDeferred] = useState(
    () => localStorage.getItem(reminderKey) === 'true'
  );

  // iOS raises the instruction sheet FIRST (user report + device pass
  // 2026-08-16). The .ics preview repeats the vCard's misdirection exactly:
  // the bright blue checkmark reads as "saved" and saves nothing — the real
  // save is the quiet floating "Add To Calendar" pill, then the check. Same
  // trap as Save-to-phone, so the same signpost (IosHandoffSheet). Android
  // goes straight through; its download lands in the calendar app.
  const [reminderSheetOpen, setReminderSheetOpen] = useState(false);

  const openReminderFile = () => {
    const hostId = profileData?.profileUserId || getHostProfileUserId();
    openReminderIcs({
      firstName: firstNameOf(profileData?.name),
      roomUrl: buildPermanentUrl(hostId),
    });
  };

  const handleThreadRemindLater = () => {
    // The sentinel writes at the TAP, not at completion — the deferral is the
    // declaration ("not now"), and both non-completion reasons (changed their
    // mind, instructed and bailed) were ruled fine to declutter on the
    // Save-to-phone precedent. Also means the link can't be tapped twice into
    // two calendar events.
    localStorage.setItem(reminderKey, 'true');
    setThreadDeferred(true);
    logEvent('thread_remind_me_later', { chatRoomId });
    if (isIOS()) {
      setReminderSheetOpen(true);
      return;
    }
    openReminderFile();
  };

  // The door out to People — the count badge beside the back chevron (user
  // redesign 2026-08-16, superseding both D11's header pill and the "Your
  // people" link that replaced it). The NUMBER is the receipt: someone who
  // just scanned a stranger wants evidence something was captured, and no
  // wording did that job as well as a glyph everyone already reads.
  //
  // POST-SHARE only, same as the link before it — pre-share the gate stays the
  // only thing to do, and a deferrer's exit is the back arrow.
  // getConnectionCount, not getConnections().length — the badge needs the
  // NUMBER, and hydrating every connection (including a full parse of every
  // stored thread) to read it was real work on the exchange hot path, fired
  // at the exact moment the share completes.
  const [connectionCount, setConnectionCount] = useState(0);
  useEffect(() => {
    if (!isThreadVariant() || !hasSharedContact) return;
    getConnectionCount()
      .then(setConnectionCount)
      .catch(() => setConnectionCount(0));
  }, [hasSharedContact, chatRoomId]);

  const goToPeople = () => {
    updateSessionChatRoomId(chatRoomId);
    navigateTo('anon-connections-list', { source: 'thread-people-badge' });
  };

  const threadPeopleBadge =
    isThreadVariant() && hasSharedContact ? (
      <ThreadPeopleBadge count={connectionCount} onClick={goToPeople} />
    ) : null;

  // No extraction: this is a dedicated name field, not the chat composer where
  // people type a greeting shape ("Hi it's Franklin") that needs unwrapping.
  const handleThreadNameSubmit = (nameValue) => handleShareBackNameSubmit(nameValue);

  // THE VALUE NEVER BUBBLES (user ruling 2026-08-20). It used to post a second
  // sent bubble carrying the raw number/handle, so the Anon's side of the
  // thread ended as two bubbles — their name, then their digits — against the
  // host's ONE contact card. The value now lives exactly where the host's
  // does: behind a card. The name bubble morphs into that card the moment this
  // resolves (ConnectionPageShared, anonContactCardName), and tapping it opens
  // the edit overlay with both fields.
  //
  // Nothing is lost from the RECORD: commitAnonContact still fires the one
  // authoritative `contact` data message, which is what the host actually
  // reads and what conversion identity-healing mines for self-declared phones
  // (migration 021). This is a presentation change only.
  const handleThreadShareComplete = async ({ contactType, contactValue }) => {
    await commitAnonContact(contactType, contactValue);
    // Funnel step 1 — 'share_back', the SAME method value TEXT logs from
    // AnonExchangeFlow, because the whole point of this arm is a head-to-head
    // read against TEXT. Without this THREAD's share happens on a different
    // page from every other variant's and produces no step-1 row at all: the
    // comparison would have been silently empty, not wrong. Deduped per room
    // inside the logger.
    logFunnelExchangeCompleted(chatRoomId, 'share_back');
    setShowThreadSheet(false);
    // The unlock is this same moment, so it gets no event of its own — a
    // 'composer unlocked' row would be a duplicate of this one under a second
    // name (the only other way to a live composer is a returner who already
    // shared, which is not an unlock).
  };

  // Docked invite banner (footer) — pure derivation, extracted to
  // deriveBannerVariant (unit-tested; refactor log row 2026-07-04)
  // Device pass 1 ruling (2026-07-16): no card auto-raises — the tappable
  // "Join Royale to keep this chat" line is the ask (pull, not push). Only a
  // real invite from the Profile lands as the card.
  const bannerVariant = deriveBannerVariant({ dotStatus, inviteMsg, userPreferences });

  // ===== EXPIRY (decision #11: offer to forget — enforced, kept light) =====
  // Rolling window (lifecycleWindows.ANON_CHAT_EXPIRY) from the room's last
  // activity (last message, else the meeting itself). Client-side gate on the
  // Anon's own VIEW only — nothing stored changes, and the Profile's record is
  // untouched (strictly one-sided). Signing up keeps the chat (decision #23) —
  // the join line promises "keep this chat," so a signed-up Anon never expires
  // (their invite may take days).
  const lastActivityMs = useMemo(() => {
    const times = chatMessages.map((m) => getTime(m.timestamp));
    times.push(getTime(connectionMetadata?.meetingDate));
    return Math.max(...times, 0);
  }, [chatMessages, connectionMetadata]);
  const chatExpired =
    !userPreferences?.hasSignedUpForQR &&
    lastActivityMs > 0 &&
    Date.now() - lastActivityMs > ANON_CHAT_EXPIRY_MS;

  // Composer sub-copy: the expiry promise during the ask stages; at completion
  // (both shared) the slot upgrades to the crown unlock pill (D7) — arrival
  // makes it noticed, volume doesn't (not black; grey vs outlined decided on
  // device via UNLOCK_PILL_VARIANT). "Keep," not "save" — the save verb
  // belongs to contact-to-phone (D2a). The existing gates ride the upgrade
  // unchanged: yields while the invite banner shows (one ask at a time),
  // hidden once signed up.
  const unlockPillStyle =
    UNLOCK_PILL_VARIANT === 'outlined'
      ? { backgroundColor: 'transparent', border: `1px solid ${COLORS.MEDIUM_DARK_GRAY}` }
      : { backgroundColor: '#F2F2F7', border: 'none' };
  // THREAD renders NO subline at all — the whole slot is the gate's ("Remind
  // me later", D10), and no THREAD frame carries either of the two things this
  // slot can otherwise hold.
  //
  // This is a TWO-BRANCH ternary, and that matters: the first attempt gated
  // only the expiry line, which made THREAD fall THROUGH to the second branch
  // and render the inline "Join Royale to keep this chat" pill — the very
  // thing it was meant to remove. Gate the whole derivation, not a branch.
  const composerSubline = isThreadVariant() ? null : morphStage &&
    !userPreferences?.hasSignedUpForQR ? (
    <span style={{ fontSize: '13px', color: '#AEAEB2' }}>
      {/* "Chat disappears" per the 2026-07-26 mocks; the time phrase is the
            D8 device-test variant, derived in lifecycleWindows (one home).
            Footnote weight (device pass 1: 15px gray banged into the gray
            placeholder above it) — the placeholder is the actionable line;
            this one whispers. The 15px contrast rule was for the old TAPPABLE
            join line in this slot, not pure information. */}
      Chat disappears {anonChatExpiryPhrase()}
    </span>
  ) : bannerVariant === null && !userPreferences?.hasSignedUpForQR ? (
    <motion.span
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: 'easeOut' }}
      onClick={handleSignupClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          borderRadius: BORDER_RADIUS.PILL,
          padding: '5px 12px',
          fontSize: '15px',
          fontWeight: 500,
          color: COLORS.CHAT_TEXT_DARK,
          ...unlockPillStyle,
        }}
      >
        <img src={CROWN_BLACK} alt="" style={{ width: '18px', height: '11px' }} />
        Join Royale
      </span>
      <span style={{ fontSize: '15px', color: '#8E8E93' }}>to keep this chat</span>
    </motion.span>
  ) : null;

  // Beta capture overlay — self-serve waitlist (closer card, "Join Royale to
  // keep your chat", the expired screen, and the non-whitelisted invite path).
  // One element, mounted from whichever return renders.
  const signupOverlayElement = (
    <LaunchSignupOverlay
      show={showSignupOverlay}
      onClose={() => setShowSignupOverlay(false)}
      onComplete={({ contactType, name }) => {
        setShowSignupOverlay(false);
        setHasSignedUpForQR(chatRoomId, true);
        // D10: joined FROM the expired screen → the room's view becomes the
        // held screen, not a silently-reopened dead chat. Pre-expiry joiners
        // never set this — their live chat keeps the never-expires exemption.
        if (signupFromExpired) setWaitlistHeld(chatRoomId, true);
        logFunnelSignupWaitlist(
          chatRoomId,
          'connection_page',
          true,
          {
            contactType,
            anonName: name,
            connectedWith: contextData?.profileData?.name || null,
            location: formatLocationShort(connectionMetadata) || null,
          },
          { skipQrGuard: true }
        );
      }}
      prefillPhone={anonPhone}
      prefillName={alreadySharedName || ''}
      startAtStage={1}
      chatRoomId={chatRoomId}
      connectedWith={contextData?.profileData?.name || null}
      // D12 final copy (refine pass 1, 2026-07-27): "Join Royale" answers the
      // tap that opens this sheet; "You're on the waitlist" implies launching
      // soon (the old "Beta launching soon" line deleted); "Your chat is
      // waiting for you" is the action confirmation that can't go — and is
      // literally true on the held screen (D10).
      title="Join Royale"
      valueProp=""
      caption="Living address book"
      submitButtonText="Join"
      thanksTitle="You're on the waitlist"
      thanksSubtitle="Your chat is waiting for you"
      thanksDwellMs={4000}
      thanksFullscreen={signupFromExpired}
    />
  );

  // Held room (D10): they joined from the expired screen — the chat waits
  // server-side (waitlist tie + conversion restore); the view says so. Takes
  // precedence over the live chat the signup exemption would otherwise reopen.
  if (userPreferences?.waitlistHeld) {
    return <ExpiredChatScreen held />;
  }

  // Past the window, the Anon's anonymous window closes (decision #11 — the
  // promise kept). Their view only; the room, and the Profile's side, persist.
  if (chatExpired) {
    return (
      <>
        {signupOverlayElement}
        <ExpiredChatScreen onJoin={handleExpiredSignupClick} />
      </>
    );
  }

  // Component render
  return (
    <>
      {/* Clear button — branch deploys only, fixed left. Raised clear of the
          composer + subline zone (user, 2026-07-27: at 80px the expiry copy /
          unlock pill made it unreachable between tests) */}
      {process.env.REACT_APP_BRANCH && process.env.REACT_APP_BRANCH !== 'main' && (
        <div
          onClick={() => {
            window.location.href = '/clear';
          }}
          style={{
            position: 'fixed',
            left: '6px',
            bottom: '170px',
            cursor: 'pointer',
            padding: '8px',
            zIndex: 5,
          }}
        >
          <Broom size={20} weight="light" color="#D8D8DC" />
        </div>
      )}
      {/* Connection Error Banner - floating iOS-style banner when confirmation times out */}
      {propConnectionError && (
        <NoticeCard
          title="Please scan the QR again!"
          line={
            <>
              The connection with {profileData?.name?.split(' ')[0] || 'them'} didn&apos;t
              work&hellip;
            </>
          }
        />
      )}
      {!propConnectionError && unsaved.length > 0 && (
        <NoticeCard
          title="Lost connection for a moment"
          line="Your card didn't save yet."
          actionLabel="Try again"
          onAction={retryUnsaved}
          busy={retrying}
        />
      )}

      {signupOverlayElement}

      {/* PWA conversion overlay — whitelisted invite CTA (phone confirmation → install instructions inline) */}
      <PendingProfileOverlay
        show={showPendingOverlay}
        onClose={() => setShowPendingOverlay(false)}
        prefillPhone={anonPhone}
      />

      {/* Share overlay — card-tap EDIT only (pre-filled), scrim per §4.2
          (user-initiated). First-share runs through the morphing input; the
          card-tap edit is the greeting-as-name recovery. */}
      <AnonShareOverlay
        show={showShareOverlay}
        onSubmit={handleUnifiedShareSubmit}
        onDismiss={() => setShowShareOverlay(false)}
        profileData={profileData}
        hostFirstName={firstNameOf(profileData?.name)}
        currentName={alreadySharedName}
        currentContactType={alreadySharedTypes[0] || ''}
        currentContactValue={sharedContactValues[alreadySharedTypes[0]] || ''}
      />

      {/* THREAD's structured share (plan 3 Step 6): Name → channel → value.
          Replaces the morph for this variant — the gate CTA raises it, and
          each step's submit posts its own bubble in the thread behind. */}
      {showThreadSheet && (
        <AnonThreadShareSheet
          profileData={profileData}
          sharedName={alreadySharedName || ''}
          onNameSubmit={handleThreadNameSubmit}
          onComplete={handleThreadShareComplete}
          onClose={() => setShowThreadSheet(false)}
        />
      )}

      {/* iOS signpost before the .ics preview. "On the next screen" because
          people don't know an iOS screen is coming at all; the row quotes the
          words to look for, and the check — a screen further on — sits below
          them (user ruling 2026-08-16). */}
      {reminderSheetOpen && (
        <IosHandoffSheet
          headline="On the next screen, tap"
          rows={['Add To Calendar']}
          footnote={
            <>
              Then
              <Check size={22} color="#1C1C1E" weight="bold" />
            </>
          }
          onOk={() => {
            setReminderSheetOpen(false);
            openReminderFile();
          }}
          onClose={() => setReminderSheetOpen(false)}
        />
      )}

      {/* Always render ConnectionPageShared */}
      <div>
        <ConnectionPageShared
          chatRoomId={chatRoomId}
          profileData={displayProfileData}
          isLoading={isLoading}
          isAnonymousUser={true}
          userType="anon"
          // THREAD: no way out until the two have actually connected. Before
          // the share there is exactly one thing to do, and a back arrow is a
          // second one. Post-share the arrow returns — and a deferrer gets it
          // too, since "Remind me later" IS the declaration of "not now, let me
          // look around" (D11's reasoning, now carried by the arrow instead of
          // the retired header pill).
          showBackButton={!isThreadVariant() || hasSharedContact || threadDeferred}
          customSpacerHeight="100px" // Add consistent spacer height for unified message display
          onImageClick={handleImageClick}
          // Add to Contacts button hidden - now only available on Detail page
          showAddToContacts={false}
          // Connection status for deleted banner logic
          connectionStatus={dotStatus}
          // Activity status for header subtitle (no location — anon can't share)
          activityStatus={activityStatus}
          // Sentence banner in/at derivation (D6)
          connectionMetadata={connectionMetadata}
          // Landing → chat reveal: waiting messages land one after another
          entranceStaggerCount={entranceStaggerCount}
          actualChatMessages={chatMessages}
          actualSetChatMessages={handleSetChatMessages}
          onGoBack={() => {
            goBack('anon-connections-list');
          }}
          // Morphing input owns the composer slot for the whole pre-contact
          // window (name stage, then the three-state contact field — which IS
          // the chat path for unclassified input). Post-classification it hands
          // back to the standard input — no customInputComponent.
          customInputComponent={
            threadLocked ? (
              <AnonThreadGate
                onShare={() => setShowThreadSheet(true)}
                onRemindLater={handleThreadRemindLater}
                showRemindLater={!threadDeferred}
              />
            ) : morphStage && !isThreadVariant() ? (
              <ChatInputMorphing
                stage={morphStage}
                // extractName: the chat name field is where people type the
                // Profile's greeting shape back ("Hi it's Franklin")
                onNameSubmit={(v) => handleShareBackNameSubmit(v, { extractName: true })}
                onContactSubmit={handleContactStageSubmit}
              />
            ) : null
          }
          // Camera unlocks WITH the composer (post-name) — supersedes the old
          // post-contact gate (refine pass 1). ChatInputNormal shows it only in
          // composer state; the morph's contact field has no camera. Under
          // THREAD the composer itself is gone until the share lands, so the
          // camera arrives with it rather than at the name step.
          onTakePhoto={
            isThreadVariant()
              ? threadLocked
                ? undefined
                : handleTakePhoto
              : alreadySharedName
                ? handleTakePhoto
                : undefined
          }
          // D3 (exchange-clarity): the morph stages carry the placeholder
          // guidance now; the post-contact standard composer stays plain —
          // "after share-back it's plain + camera"
          chatPlaceholder=""
          composerSubline={composerSubline}
          threadHeader={isThreadVariant()}
          headerBackBadge={threadPeopleBadge}
          hideMeetingBanner={hideMeetingBanner}
          // Get my QR button removed - now only available on AnonConnectionsListPage
          showGetOwnQRButton={false}
          onGetOwnQRClick={null}
          // Anon taps own name bubble → edit overlay (greeting-as-name recovery)
          onAnonCardClick={openShareOverlay}
          // Once the contact has committed, the Anon's own name bubble morphs
          // into their contact card (user design 2026-08-20). DERIVED, unlike
          // the marker it replaced: this is a durable property of the thread,
          // not a moment — a returner must find the card already there, and
          // hasSharedContact is exactly "their value is on the record".
          anonContactCardName={hasSharedContact ? alreadySharedName || '' : null}
          // Docked invite CTA banner, above the chat input (small text X = permanent close).
          // THREAD shows it only on a real invite — gating lives in shouldDockBanner (see its JSDoc).
          footerBanner={
            shouldDockBanner({ isThread: isThreadVariant(), bannerVariant }) ? (
              <WelcomeInviteBanner
                variant={bannerVariant}
                inviterName={displayProfileData?.name || null}
                onJoin={handleBannerJoin}
                onDismiss={() => dismissBanner(chatRoomId, 'invite')}
              />
            ) : null
          }
        />
      </div>
    </>
  );
};

// PERFORMANCE: Memoize to prevent re-renders when parent (AnonExchangeFlow) state changes
// but props haven't changed (e.g., during loadingStage transitions)
const MemoizedAnonConnectionPage = memo(AnonConnectionPage);
MemoizedAnonConnectionPage.displayName = 'AnonConnectionPage';

export default MemoizedAnonConnectionPage;
